import { auth } from '../config'
import { 
  createUserWithEmailAndPassword, 
  signInWithEmailAndPassword,
  signOut as firebaseSignOut,
  UserCredential,
  deleteUser as firebaseDeleteUser,
  getAuth,
  initializeAuth,
  inMemoryPersistence
} from 'firebase/auth'
import { doc, setDoc, getDoc, deleteDoc, updateDoc as firestoreUpdateDoc, runTransaction, collection, getDocs } from 'firebase/firestore'
import { db, app } from '../config'

export interface UserData {
  name: string
  email: string
  role: 'admin' | 'manager' | 'user'
  document: string
  profileId: string
  organizations?: string[] // IDs das organizações que o usuário tem acesso
  active?: boolean
  createdAt?: string
  updatedAt?: string
  twoFactorEnabled?: boolean
  twoFactorSecret?: string
  twoFactorUpdatedAt?: string
}

export const createUser = async (userData: UserData, password: string): Promise<{uid: string}> => {
  console.log('[Auth Service] Iniciando criação de usuário:', { 
    email: userData.email, 
    name: userData.name,
    profileId: userData.profileId,
    organizations: userData.organizations
  });
  
  try {
    // Validar dados obrigatórios
    if (!userData.email || !userData.name || !userData.profileId || !userData.organizations?.length) {
      throw new Error('Dados obrigatórios faltando: email, nome, perfil e organizações são obrigatórios');
    }

    // 1. Criar usuário no Authentication usando apenas a API REST
    const response = await fetch(`https://identitytoolkit.googleapis.com/v1/accounts:signUp?key=${app.options.apiKey}`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        email: userData.email,
        password: password,
        displayName: userData.name,
        returnSecureToken: false // Importante: não retornar token para evitar auto-login
      })
    });

    const data = await response.json();
    if (data.error) {
      throw new Error(data.error.message);
    }

    const uid = data.localId;
    console.log('[Auth Service] Usuário criado no Authentication:', uid);

    // 2. Salvar dados do usuário no Firestore usando REST API também
    const userDataToSave = {
      ...userData,
      uid,
      email: userData.email.toLowerCase(),
      active: true,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
      twoFactorEnabled: false,
      twoFactorSecret: null,
      role: userData.role || 'user',
      profileId: userData.profileId,
      organizations: userData.organizations
    };

    // Usar setDoc com merge para evitar sobrescrever dados existentes
    await setDoc(doc(db, 'users', uid), userDataToSave, { merge: true });
    console.log('[Auth Service] Documento do usuário criado com sucesso');

    return { uid };
  } catch (error) {
    console.error('[Auth Service] Erro ao criar usuário:', error);
    throw error;
  }
};

export const signIn = async (email: string, password: string) => {
  console.log('[Auth Service] Tentando login:', { email })
  
  try {
    // 1. Fazer login
    const userCredential = await signInWithEmailAndPassword(auth, email.toLowerCase(), password)
    console.log('[Auth Service] Login bem sucedido:', userCredential.user.uid)

    // 2. Verificar se o usuário existe no Firestore
    const userDoc = await getDoc(doc(db, 'users', userCredential.user.uid))
    if (!userDoc.exists()) {
      console.error('[Auth Service] Usuário não encontrado no Firestore')
      await auth.signOut()
      throw new Error('Usuário não encontrado')
    }

    // 3. Verificar se o usuário está ativo
    const userData = userDoc.data()
    if (!userData.active) {
      console.error('[Auth Service] Usuário inativo')
      await auth.signOut()
      throw new Error('Usuário inativo')
    }

    console.log('[Auth Service] Login completo com sucesso')
    return userCredential
    
  } catch (error) {
    console.error('[Auth Service] Erro no login:', error)
    throw error
  }
}

export const signOut = async () => {
  try {
    console.log('[Auth Service] Iniciando logout...');
    await firebaseSignOut(auth);
    console.log('[Auth Service] Logout completo');
  } catch (error) {
    console.error('[Auth Service] Erro no logout:', error);
    throw error;
  }
}

export const deleteUserAuth = async (uid: string) => {
  try {
    // Primeiro, verificamos se o usuário atual é admin
    const currentUser = getAuth().currentUser
    if (!currentUser) {
      throw new Error('No user logged in')
    }

    const adminDoc = await getDoc(doc(db, 'users', currentUser.uid))
    if (!adminDoc.exists() || adminDoc.data()?.role !== 'admin') {
      throw new Error('Only admin users can delete other users')
    }

    // Inativar o usuário no Firestore
    const userRef = doc(db, 'users', uid)
    await firestoreUpdateDoc(userRef, {
      active: false,
      updatedAt: new Date().toISOString(),
      deletedAt: new Date().toISOString(),
      deletedBy: currentUser.uid
    })

    // Forçar logout se o usuário deletado estiver logado
    const deletedUser = getAuth().currentUser
    if (deletedUser && deletedUser.uid === uid) {
      await firebaseSignOut(auth)
    }

    return true
  } catch (error) {
    console.error('Error deleting user:', error)
    throw error
  }
}

export const deleteUser = async (userId: string) => {
  console.log('[Auth Service] Iniciando exclusão de usuário:', userId);
  
  try {
    // 1. Excluir documento do usuário no Firestore
    await deleteDoc(doc(db, 'users', userId));
    console.log('[Auth Service] Documento do usuário excluído');

    // 2. Excluir usuário no Authentication
    const user = auth.currentUser;
    if (user) {
      await firebaseDeleteUser(user);
      console.log('[Auth Service] Usuário excluído do Authentication');
    }

    return true;
  } catch (error) {
    console.error('[Auth Service] Erro ao excluir usuário:', error);
    throw error;
  }
};

export const blockUser = async (userId: string, blocked: boolean) => {
  console.log('[Auth Service] ' + (blocked ? 'Bloqueando' : 'Desbloqueando') + ' usuário:', userId);
  
  try {
    // 1. Atualizar status no Firestore
    const userRef = doc(db, 'users', userId);
    await firestoreUpdateDoc(userRef, {
      active: !blocked,
      updatedAt: new Date().toISOString()
    });
    console.log('[Auth Service] Status do usuário atualizado no Firestore');

    return true;
  } catch (error) {
    console.error('[Auth Service] Erro ao ' + (blocked ? 'bloquear' : 'desbloquear') + ' usuário:', error);
    throw error;
  }
};